/**
 * @fileoverview Upload helpers for the Documents Library.
 *
 * Houses the accepted-extension filter and the recursive DataTransfer
 * collection logic (moved from the retired dropzone uploader component),
 * extended to preserve each file's directory chain so folder drops can
 * recreate their structure as Library folders.
 */

/**
 * File extensions we accept (used to filter folder contents).
 * Individual emails (.eml/.msg) are accepted (MOR-899); mailbox archives
 * (.pst) are not — the server rejects bulk email imports.
 */
export const ACCEPTED_EXTENSIONS = new Set([
  ".pdf",
  ".xlsx",
  ".xls",
  ".csv",
  ".docx",
  ".doc",
  ".jpg",
  ".jpeg",
  ".png",
  ".msg",
  ".eml",
  ".txt",
  ".md",
  ".json",
  ".xml",
  ".log",
]);

/** MIME fallbacks by extension for types browsers commonly leave blank. */
const MIME_BY_EXTENSION: Record<string, string> = {
  eml: "message/rfc822",
  msg: "application/vnd.ms-outlook",
};

/**
 * MIME type to upload a file under. Browsers report an empty type for
 * Outlook email files, so fall back to the extension before the generic
 * octet-stream default.
 */
export function uploadMimeType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  return MIME_BY_EXTENSION[ext] ?? "application/octet-stream";
}

/**
 * Whether a single file passes the accepted-type filter.
 * Skips hidden files/directories (containing /. or starting with .)
 */
export function isAcceptedFile(file: File): boolean {
  // Skip hidden files and files inside hidden directories
  if (file.name.startsWith(".")) return false;
  if (file.webkitRelativePath?.includes("/.")) return false;

  const ext = "." + file.name.split(".").pop()?.toLowerCase();
  return ACCEPTED_EXTENSIONS.has(ext);
}

/**
 * Filters files to only include accepted file types based on extension.
 */
export function filterAcceptedFiles(files: File[]): File[] {
  return files.filter(isAcceptedFile);
}

/** A collected file plus the directory chain it was dropped inside. */
export interface CollectedFile {
  file: File;
  /**
   * Names of the directories between the drop target and the file, outermost
   * first. Empty for files dropped directly (not inside a folder).
   */
  relativeDirs: string[];
}

export interface CollectWithPathsResult {
  files: CollectedFile[];
  errors: number;
}

/**
 * Recursively collects all files from a DataTransferItemList, traversing
 * directories via the webkitGetAsEntry() API and preserving each file's
 * directory chain relative to the drop.
 *
 * Returns both the collected files and a count of entries that could not
 * be read (stale handles, permission errors, etc.).
 */
export async function collectFilesWithPaths(
  items: DataTransferItemList,
): Promise<CollectWithPathsResult> {
  const files: CollectedFile[] = [];
  const promises: Promise<boolean>[] = [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const entry = item.webkitGetAsEntry?.();

    if (entry) {
      promises.push(
        traverseEntryWithPath(entry, [], files)
          .then(() => false)
          .catch((err) => {
            console.warn("Skipped unreadable entry:", entry.name, err);
            return true;
          }),
      );
    } else {
      const file = item.getAsFile();
      if (file) files.push({ file, relativeDirs: [] });
    }
  }

  const outcomes = await Promise.all(promises);
  const errors = outcomes.filter(Boolean).length;
  return { files, errors };
}

/**
 * Recursively traverses a FileSystemEntry, collecting files along with the
 * directory names accumulated from the drop root. Individual file/directory
 * read errors are caught so one bad entry doesn't abort the traversal.
 * Dot-prefixed entries (hidden files/folders) are skipped.
 */
async function traverseEntryWithPath(
  entry: FileSystemEntry,
  dirs: string[],
  files: CollectedFile[],
): Promise<void> {
  if (entry.name.startsWith(".")) return;

  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry;
    try {
      const file = await new Promise<File>((resolve, reject) => {
        fileEntry.file(resolve, reject);
      });
      files.push({ file, relativeDirs: dirs });
    } catch (err) {
      console.warn(`Could not read file "${entry.name}":`, err);
    }
  } else if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntry;
    const reader = dirEntry.createReader();
    const childDirs = [...dirs, entry.name];

    try {
      let batch: FileSystemEntry[] = [];
      do {
        batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
          reader.readEntries(resolve, reject);
        });
        const childPromises = batch.map((child) =>
          traverseEntryWithPath(child, childDirs, files).catch((err) => {
            console.warn(`Skipped entry "${child.name}":`, err);
          }),
        );
        await Promise.all(childPromises);
      } while (batch.length > 0);
    } catch (err) {
      console.warn(`Could not read directory "${entry.name}":`, err);
    }
  }
}
